// ForgotPassword.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/Product_logo.png";
import { API_URL } from "../config";
import { showNotification } from "../utils/notificationService";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async () => {
        if (!email) {
            showNotification("Please enter your email address", "error");
            return;
        }

        try {
            setLoading(true);

            const response = await fetch(`${API_URL}/auth/forgot-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || "Failed to send reset email");
            }

            setSubmitted(true);
        } catch (error) {
            showNotification(error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.card}>
                <img src={logo} alt="Logo" style={styles.logo} />
                <h2 style={styles.heading}>Forgot Password</h2>

                {submitted ? (
                    <>
                        <div style={styles.successIcon}>✉️</div>
                        <p style={styles.successTitle}>Check your email</p>
                        <p style={styles.successText}>
                            We've sent a password reset link to <strong>{email}</strong>.
                            Please check your inbox and follow the instructions.
                        </p>
                        <Link to="/" style={styles.backBtn}>
                            Back to Login
                        </Link>
                    </>
                ) : (
                    <>
                        <p style={styles.subtitle}>
                            Enter your registered email address and we'll send you a link to
                            reset your password.
                        </p>

                        <div style={styles.inputContainer}>
                            <input
                                type="email"
                                placeholder="Email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                                style={styles.input}
                            />
                        </div>

                        <button
                            style={{
                                ...styles.submitBtn,
                                opacity: loading ? 0.7 : 1,
                                cursor: loading ? "not-allowed" : "pointer",
                            }}
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? "Sending..." : "Send Reset Link"}
                        </button>

                        <p style={styles.backLink}>
                            <Link to="/" style={styles.link}>
                                ← Back to Login
                            </Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

/* ================= STYLES ================= */

const styles = {
    wrapper: {
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 40%, #eef2ff 100%)",
    },
    card: {
        width: 380,
        background: "#fff",
        padding: "32px 28px",
        borderRadius: 16,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        textAlign: "center",
    },
    logo: {
        width: 100,
        height: 120,
        marginBottom: 1,
    },
    heading: {
        fontSize: 22,
        fontWeight: 600,
        marginBottom: 4,
        marginTop: 12,
    },
    subtitle: {
        fontSize: 14,
        color: "#6b7280",
        marginBottom: 22,
        lineHeight: 1.5,
    },
    inputContainer: {
        display: "flex",
        alignItems: "center",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        height: 44,
        marginBottom: 16,
    },
    input: {
        flex: 1,
        height: "100%",
        border: "none",
        padding: "0 12px",
        fontSize: 14,
        outline: "none",
        borderRadius: 8,
    },
    submitBtn: {
        width: "100%",
        height: 44,
        background: "#2563eb",
        color: "#fff",
        border: "none",
        borderRadius: 8,
        fontSize: 15,
        fontWeight: 500,
        cursor: "pointer",
    },
    backLink: {
        marginTop: 16,
        fontSize: 13,
    },
    link: {
        color: "#6b7280",
        textDecoration: "none",
        fontSize: 13,
    },
    // Success state
    successIcon: {
        fontSize: 48,
        marginBottom: 12,
        marginTop: 8,
    },
    successTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: "#111827",
        marginBottom: 8,
    },
    successText: {
        fontSize: 14,
        color: "#6b7280",
        lineHeight: 1.6,
        marginBottom: 24,
    },
    backBtn: {
        display: "inline-block",
        padding: "10px 24px",
        background: "#2563eb",
        color: "#fff",
        borderRadius: 8,
        textDecoration: "none",
        fontSize: 14,
        fontWeight: 500,
    },
};
